import React from 'react'

import { useTranslation } from 'react-i18next'
import styled, { keyframes } from 'styled-components'

import { useAudioStore } from 'context/AudioContext/AudioContext'
import { Icons } from 'helpers/icons'
import colors from 'styles/colors'

interface IAudioPlayButtonProps {
    audioRef: React.MutableRefObject<HTMLAudioElement | null>
}

export const AudioPlayButton = ({ audioRef }: IAudioPlayButtonProps) => {
    const state = useAudioStore()

    const {
        audioReady,
        playing,
        setPlaying,
        fullyListened,
        setFullyListened,
        setListened
    } = state

    const { t } = useTranslation()

    const onPause = (event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation()

        if (audioRef?.current) {
            audioRef?.current?.pause()
        }

        setPlaying(false)
    }

    const onPlay = (event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation()

        if (fullyListened) {
            setListened(0)
            setFullyListened(false)
        }

        if (audioRef?.current) {
            audioRef?.current?.play()
        }

        setPlaying(true)
    }

    if (!audioReady) {
        return (
            <PreviewLoader>
                <ProcessingFileLoader />
            </PreviewLoader>
        )
    }

    return (
        <PreviewPlayWrapper
            key={playing ? 'playing' : 'not-playing'}
            onClick={e => (playing ? onPause(e) : onPlay(e))}
        >
            <PreviewPlayImage
                src={playing ? Icons.stop : Icons.play}
                alt={t('play')}
            />
        </PreviewPlayWrapper>
    )
}

const PreviewPlayImage = styled.img`
    height: 2rem !important;
    width: 2rem !important;
    color:white;
`

const PreviewPlayWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 3rem;
    width: 3rem;
    margin-left: 1rem;
    border-radius: 1rem;
    background: ${colors.blue92deg};
    margin-left: auto;
    cursor: pointer;
`

const PreviewLoader = styled.span`
    display: flex;
    justify-content: center;
    align-items: center;
    font-weight: 500;
    text-align: right;
    color: white;
    font-size: 1rem;
    margin-right: 0.3rem;
    width: 6rem;
`

const spinAnimation = keyframes`
  0%,
  100% {
      box-shadow:
          0 -3em 0 0.2em,
          2em -2em 0 0em,
          3em 0 0 -1em,
          2em 2em 0 -1em,
          0 3em 0 -1em,
          -2em 2em 0 -1em,
          -3em 0 0 -1em,
          -2em -2em 0 0;
  }
  12.5% {
      box-shadow:
          0 -3em 0 0,
          2em -2em 0 0.2em,
          3em 0 0 0,
          2em 2em 0 -1em,
          0 3em 0 -1em,
          -2em 2em 0 -1em,
          -3em 0 0 -1em,
          -2em -2em 0 -1em;
  }
  25% {
      box-shadow:
          0 -3em 0 -0.5em,
          2em -2em 0 0,
          3em 0 0 0.2em,
          2em 2em 0 0,
          0 3em 0 -1em,
          -2em 2em 0 -1em,
          -3em 0 0 -1em,
          -2em -2em 0 -1em;
  }
  37.5% {
      box-shadow:
          0 -3em 0 -1em,
          2em -2em 0 -1em,
          3em 0em 0 0,
          2em 2em 0 0.2em,
          0 3em 0 0em,
          -2em 2em 0 -1em,
          -3em 0em 0 -1em,
          -2em -2em 0 -1em;
  }
  50% {
      box-shadow:
          0 -3em 0 -1em,
          2em -2em 0 -1em,
          3em 0 0 -1em,
          2em 2em 0 0em,
          0 3em 0 0.2em,
          -2em 2em 0 0,
          -3em 0em 0 -1em,
          -2em -2em 0 -1em;
  }
  62.5% {
      box-shadow:
          0 -3em 0 -1em,
          2em -2em 0 -1em,
          3em 0 0 -1em,
          2em 2em 0 -1em,
          0 3em 0 0,
          -2em 2em 0 0.2em,
          -3em 0 0 0,
          -2em -2em 0 -1em;
  }
  75% {
      box-shadow:
          0em -3em 0 -1em,
          2em -2em 0 -1em,
          3em 0em 0 -1em,
          2em 2em 0 -1em,
          0 3em 0 -1em,
          -2em 2em 0 0,
          -3em 0em 0 0.2em,
          -2em -2em 0 0;
  }
  87.5% {
      box-shadow:
          0em -3em 0 0,
          2em -2em 0 -1em,
          3em 0 0 -1em,
          2em 2em 0 -1em,
          0 3em 0 -1em,
          -2em 2em 0 0,
          -3em 0em 0 0,
          -2em -2em 0 0.2em;
  }
`

const ProcessingFileLoader = styled.div`
    display: block;
    color: #000;
    font-size: 0.4rem;
    width: 1em;
    height: 1em;
    border-radius: 50%;
    position: relative;
    text-indent: -9999em;
    animation: ${spinAnimation} 1.3s infinite linear;
    transform: translateZ(0);
`
